import React, { useEffect, useState } from "react";
import { Button, Image, message, Modal, Spin } from "antd";
import {
  CheckCircleFilled,
  LoadingOutlined,
  LoginOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import apiClient from "services/apiClient";
import { useDispatch } from "react-redux";
import { logout } from "store/slices/authSlice";
import { useMyContext } from "Context/MyContextProvider";
import { useFacebookSdk } from "../../Hooks/useFacebookSdk";

const LOADING_MESSAGES = [
  "Connecting to Meta Business API...",
  "Verifying security credentials...",
  "Securing access tokens...",
  "Configuring WhatsApp Business sandbox...",
  "Setting up Webhook event dispatchers...",
  "Finalizing integration workspace..."
];

const Embedded = ({ title }) => {
  const { Permisson, removeFcmToken } = useMyContext();

  const fbButtonStyle = {
    backgroundColor: "#1877f2",
    border: 0,
    borderRadius: "8px",
    color: "#fff",
    cursor: "pointer",
    fontFamily: "Helvetica, Arial, sans-serif",
    fontSize: "15px",
    fontWeight: "bold",
    height: "42px",
    padding: "0 20px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 14px rgba(24,119,242,0.4)",
    transition: "all 0.2s ease",
  };

  const [open, setOpen] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [disable, setDisable] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    if (onboardingDone || !open) return;

    const messageStr = LOADING_MESSAGES[currentMessageIndex];
    let charIndex = 1;
    setDisplayedText(messageStr.slice(0, 1));

    let timeoutId;
    const intervalId = setInterval(() => {
      if (charIndex <= messageStr.length) {
        setDisplayedText(messageStr.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(intervalId);
        timeoutId = setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
        }, 2000);
      }
    }, 45);

    return () => {
      clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentMessageIndex, onboardingDone, open]);

  const completeOnboarding = async (code) => {
    setOpen(true);
    try {
      const response = await apiClient.post("messaging/onboarding/complete", { code });
      if (response.data.status) {
        setOnboardingDone(true);
        setDisable(false);
        setSuccessMessage(response.data.message || "Onboarding completed successfully!");
      } else {
        message.error(response.data.message || "Onboarding failed. Please try again.");
        setOpen(false);
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
      message.error("An error occurred during onboarding. Please try again.");
      setOpen(false);
    }
  };

  const { loginWithFacebook } = useFacebookSdk({
    onSuccess: (code) => {
      console.log("Meta Embedded Signup - Temp Token (code):", code);
      completeOnboarding(code);
    },
    onCancel: (currentStep) => {
      message.warning(`Signup cancelled at step: ${currentStep}`);
    },
    onError: (errorMessage) => {
      message.error(`Error during signup: ${errorMessage}`);
    },
    Permisson,
  });

  const dispatch = useDispatch();

  const handleLogout = () => {
    removeFcmToken();
    dispatch(logout());
  };

  return (
    <div>
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}} />

      <Modal
        open={open}
        closable={false}
        footer={null}
        destroyOnClose={true}
        width={480}
        styles={{
          content: { padding: 0, borderRadius: 16, overflow: "hidden" },
        }}
      >
        {/* Gradient Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #22af4b 0%, #1b8f3d 100%)",
            padding: "24px 28px 20px",
            position: "relative",
            overflow: "hidden",
            borderRadius: "16px 16px 0 0",
          }}
        >
          {/* Decorative circles */}
          <div style={{
            position: "absolute", top: -30, right: -30,
            width: 120, height: 120, borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }} />
          <div style={{
            position: "absolute", bottom: -20, left: 60,
            width: 80, height: 80, borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ThunderboltFilled style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                Connecting With Meta
              </div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginTop: 2 }}>
                WhatsApp Business Integration
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "28px 28px 24px" }}>
          {onboardingDone ? (
            /* ── SUCCESS STATE ── */
            <div>
              {/* Success icon */}
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 72, height: 72, borderRadius: "50%",
                  background: "linear-gradient(135deg, #f0fdf4, #e6f9ed)",
                  boxShadow: "0 8px 24px rgba(34,175,75,0.2)",
                  marginBottom: 12,
                }}>
                  <CheckCircleFilled style={{ color: "#22af4b", fontSize: 38 }} />
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#1a1a2e",
                    marginBottom: 4,
                  }}
                >
                  Congratulations! Setup Complete! 🎉
                </div>
              </div>

              {/* Message card */}
              <div style={{
                background: "linear-gradient(135deg, #f0fdf4, #e6f9ed)",
                border: "1px solid #bbf7d0",
                borderRadius: 12,
                padding: "16px 18px",
                marginBottom: 24,
                position: "relative",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: 0,
                  width: 4, height: "100%",
                  background: "linear-gradient(180deg, #22af4b, #1b8f3d)",
                  borderRadius: "12px 0 0 12px",
                }} />
                <p style={{
                  margin: 0,
                  fontSize: 13.5,
                  lineHeight: 1.7,
                  color: "#2d3748",
                  paddingLeft: 8,
                }}>
                  {successMessage}
                </p>
              </div>

              {/* Logo / Meta Partner Seal */}
              <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}>
                <Image
                  src="/img/others/embedded_img.jpeg"
                  preview={false}
                  height={60}
                  style={{ objectFit: "contain" }}
                />
              </div>

              <Button
                type="primary"
                onClick={handleLogout}
                disabled={disable}
                icon={<LoginOutlined />}
                style={{
                  width: "100%",
                  height: "44px",
                  borderRadius: 10,
                  border: "none",
                  background: disable
                    ? "#d9d9d9"
                    : "linear-gradient(135deg, #22af4b, #1b8f3d)",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: disable ? "none" : "0 6px 18px rgba(34,175,75,0.4)",
                  transition: "all 0.2s ease",
                  letterSpacing: "0.3px",
                }}
              >
                Save & Re-Login
              </Button>
            </div>
          ) : (
            /* ── LOADING STATE ── */
            <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
              {/* Animated loader ring */}
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 80, height: 80, borderRadius: "50%",
                background: "linear-gradient(135deg, #eaf9ee, #d1f5da)",
                boxShadow: "0 8px 24px rgba(34,175,75,0.15)",
                marginBottom: 20,
              }}>
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 38, color: "#22af4b" }}
                      spin
                    />
                  }
                />
              </div>

              <div style={{ fontWeight: 700, fontSize: 17, color: "#1a1a2e", marginBottom: 6 }}>
                Setting up your account...
              </div>
              <div style={{ fontSize: 13, color: "#8c8c8c", marginBottom: 20 }}>
                Please wait while we connect your WhatsApp Business account with Meta.
              </div>

              {/* Logo / Meta Partner Seal */}
              <div style={{ marginBottom: 24, display: "flex", justifyContent: "center" }}>
                <Image
                  src="/img/others/embedded_img.jpeg"
                  preview={false}
                  height={60}
                  style={{ objectFit: "contain" }}
                />
              </div>

              {/* Typewriter message card */}
              <div style={{
                background: "linear-gradient(135deg, #f0fdf4, #e6f9ed)",
                border: "1px dashed #bbf7d0",
                borderRadius: 12,
                padding: "16px 20px",
                minHeight: "56px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 10px",
                boxShadow: "inset 0 2px 4px rgba(34,175,75,0.02)",
              }}>
                <span style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "#166534",
                  fontFamily: "monospace, Courier New, Courier",
                  letterSpacing: "0.2px",
                }}>
                  {displayedText}
                  <span className="typewriter-cursor" style={{
                    animation: "blink 0.8s infinite",
                    marginLeft: 2,
                    fontWeight: "bold",
                    color: "#22af4b",
                  }}>|</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Button
        type="primary"
        size={'small'}
        className="mx-2"
        style={fbButtonStyle}
        onClick={() => loginWithFacebook()}
        icon={
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        }
      >
        {title ? title : "Login with Facebook"}
      </Button>
    </div>
  );
};

export default Embedded;
